/**
 * <s-gallery-slider> — shows one product photo at a time. Arrows, the line
 * indicators, arrow keys and a horizontal swipe move between photos. The new
 * photo is revealed by a soft curtain wipe while it settles from a slow push-in;
 * the old one dims back into black. Direction follows the move (and the page's
 * reading direction), so going back wipes the other way.
 */
const SWIPE_PX = 40;

class SGallerySlider extends HTMLElement {
  #ac;
  #index = 0;
  #slides = [];

  connectedCallback() {
    const track = this.querySelector('[data-slider]');
    if (!track) return;
    this.#slides = Array.from(track.children);
    if (this.#slides.length < 2) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.#slides.forEach((slide, i) => {
      slide.toggleAttribute('data-active', i === 0);
      if (i !== 0) slide.inert = true;
    });
    this.#sync();

    this.querySelector('[data-slider-prev]')?.addEventListener('click', () => this.go(this.#index - 1, -1), opts);
    this.querySelector('[data-slider-next]')?.addEventListener('click', () => this.go(this.#index + 1, 1), opts);
    this.querySelectorAll('[data-slider-dot]').forEach((dot) => {
      dot.addEventListener('click', () => {
        const to = Number(dot.getAttribute('data-slider-dot'));
        this.go(to, to > this.#index ? 1 : -1);
      }, opts);
    });

    this.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      const rtl = getComputedStyle(this).direction === 'rtl';
      const forward = (event.key === 'ArrowRight') !== rtl;
      event.preventDefault();
      this.go(this.#index + (forward ? 1 : -1), forward ? 1 : -1);
    }, opts);

    let startX = 0;
    let startY = 0;
    let swiped = false;
    track.addEventListener('pointerdown', (event) => {
      startX = event.clientX;
      startY = event.clientY;
      swiped = false;
    }, opts);
    track.addEventListener('pointerup', (event) => {
      const dx = event.clientX - startX;
      const dy = event.clientY - startY;
      if (Math.abs(dx) < SWIPE_PX || Math.abs(dx) < Math.abs(dy)) return;
      swiped = true;
      const rtl = getComputedStyle(this).direction === 'rtl';
      const forward = (dx < 0) !== rtl;
      this.go(this.#index + (forward ? 1 : -1), forward ? 1 : -1);
    }, opts);
    // A swipe must not also open the zoom view.
    track.addEventListener('click', (event) => {
      if (!swiped) return;
      event.preventDefault();
      event.stopPropagation();
      swiped = false;
    }, { capture: true, signal: this.#ac.signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  /** @param {number} to @param {1|-1} dir */
  go(to, dir) {
    const count = this.#slides.length;
    const next = (to + count) % count;
    if (next === this.#index) return;
    const current = this.#slides[this.#index];
    const incoming = this.#slides[next];

    this.dataset.dir = dir > 0 ? 'next' : 'prev';
    this.#slides.forEach((slide) => slide.removeAttribute('data-leaving'));
    current.removeAttribute('data-active');
    current.setAttribute('data-leaving', '');
    current.inert = true;
    incoming.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
    incoming.inert = false;
    // Restart the reveal even when the same slide comes back quickly.
    incoming.removeAttribute('data-active');
    void incoming.offsetWidth;
    incoming.setAttribute('data-active', '');

    this.#index = next;
    this.#sync();
  }

  #sync() {
    const current = this.querySelector('[data-slider-current]');
    if (current) current.textContent = String(this.#index + 1).padStart(2, '0');
    this.querySelectorAll('[data-slider-dot]').forEach((dot, i) => {
      dot.toggleAttribute('data-active', i === this.#index);
      if (i === this.#index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }
}

if (!customElements.get('s-gallery-slider')) customElements.define('s-gallery-slider', SGallerySlider);
